<template>
  <b-container
    fluid
    class="sensor-general-container px-0"
  >
    <b-row>
      <filters
        :loading="loading"
        :variables="variables"
        @apply-filter="applyFilter"
        @toggle-hidden-variables="toggleHiddenVariables"
        @search-change="searchChange"
      />
    </b-row>
    <b-row
      class="px-2 mt-3"
      align-v="center"
      align-h="between"
    >
      <b-col cols="auto">
        <b-button
          v-if="$hasUserPermission('PER015')"
          @click="createGroup"
          variant="outline-primary"
          :disabled="disableNewGroup"
        >
          <s-icon
            name="add"
            class="mr-2"
          />
          {{ $t('actions.new-group') }}
        </b-button>
        <b-dropdown
          no-caret
          size="lg"
          variant="link"
          :disabled="loading"
          v-if="$hasUserPermission('PER015')"
        >
          <template #button-content>
            <s-icon
              class="pb-1"
              name="options-menu"
            />
          </template>
          <template>
            <b-dropdown-item
              href="#"
              @click="openSortGroupModal"
            >
              <span>{{ $t('supervision.groups-exhibition-order') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              href="#"
              @click="openDefaultGroupsModal"
            >
              <span>{{ $t('supervision.restore-groups-to-factory-default') }}</span>
            </b-dropdown-item>
          </template>
        </b-dropdown>
      </b-col>
      <b-col cols="auto" align-self="end">
        <collapse-expand-dropdown
          :initialize-expanded="false"
          @click-option="showAllHandler"
        />
      </b-col>
    </b-row>
    <b-row align-h="center" class="px-2">
      <s-loader v-if="loading" size="lg" class="mt-4"/>
      <b-col
        cols="12"
        :key="group.id"
        v-for="(group, index) in groupList"
      >
        <group-accordion
          class="my-3"
          ref="accordion"
          :group="group"
          :showHiddenVariables="showHiddenVariables"
          :group-list="groupWithoutReading"
          :requesting-data="requestingData"
          @on-hidden-handler="onHiddenHandler(index)"
          @added-variable="handleAdded"
          @sorted-variable="handleSorted"
          @quit-renaming="quitRenaming"
          @reset-variable="resetVariable"
          @rename-group-request="renameGroupRequest"
          @removed-variable="handleRemoval"
          @toggle-variable-visibility="toggleVisibility"
          @delete-group-request="deleteGroupRequest"
          @rename-variable-request="renameVariableRequest"
          @moved-variable-by-drag="changeVariableGroup"
          @change-variable-group-by-button="changeVariableGroup"
        />
      </b-col>
    </b-row>
    <sort-group-modal
      v-model="showSortGroupModal"
      :groups="groupList"
      :assetModuleId="assetModuleId"
    />
    <s-modal
      class="group-management-modal"
      v-model="showConfirmationRestoreDefaultGroupsModal"
      size="md"
      :title="$t('messages.MC060.title')"
      :ok-title="$t('general.confirm')"
      :confirm-type="confirmationTypes.OK_CANCEL"
      @cancel="closeModalRestoreGroups"
      @ok="restoreDefaultGroupsModal"
    >
      <p>{{ $t('messages.MC060.message') }}</p>
    </s-modal>
  </b-container>
</template>
<script>
import CollapseExpandDropdown from '@/components/buttons/CollapseExpandDropdown';
import SortGroupModal from '@/domain/components/installation/supervision/sensors/SortGroupModal';
import ConfirmationTypes from '@/constants/confirmationTypes';
import Filters from './Filters';
import GroupAccordion from './GroupAccordion';

export default {
  name: 'GeneralAndParametersLayout',
  components: {
    CollapseExpandDropdown,
    Filters,
    GroupAccordion,
    SortGroupModal,
  },
  props: {
    selectedVariables: {
      type: Array,
    },
    variables: {
      type: Array,
    },
    loading: {
      type: Boolean,
      required: true,
      default: false,
    },
    groupList: {
      type: Array,
    },
    requestingData: {
      type: Boolean,
      default: false,
    },
    assetModuleId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    variableMovedPayload: {},
    showHiddenVariables: false,
    showSortGroupModal: false,
    showConfirmationRestoreDefaultGroupsModal: false,
    confirmationTypes: ConfirmationTypes,
  }),
  computed: {
    disableNewGroup() {
      const names = this.groupList.map(({ name }) => name);
      return names.includes(null) || names.includes('');
    },
    groupWithoutReading() {
      return this.groupList.map(({ name, id, type }) => ({
        name,
        type,
        id,
      }));
    },
    dropdownItems() {
      return [
        {
          text: this.$t('actions.edit-group'),
          iconClass: 'fas fa-pencil-alt mr-2',
        },
        {
          text: this.$t('actions.delete-group'),
          iconClass: 'far fa-trash-alt mr-2',
          textColor: 'text-danger',
        },
      ];
    },
  },
  methods: {
    onHiddenHandler(index) {
      if (index === 0) {
        this.$refs.accordion[0].collapseHandler(true);
      }
    },
    toggleHiddenVariables(value) {
      this.showHiddenVariables = value;
    },
    createGroup(payload) {
      this.$emit('create-group', payload);
    },
    resetVariable(payload) {
      this.$emit('reset-variable', payload);
    },
    renameGroupRequest(payload) {
      this.$emit('rename-group-request', payload);
    },
    toggleVisibility(payload) {
      this.$emit('toggle-variable-visibility', payload);
    },
    deleteGroupRequest(payload) {
      this.$emit('delete-group-request', payload);
    },
    renameVariableRequest(payload) {
      this.$emit('rename-variable-request', payload);
    },
    indexFinder(newList, baseList) {
      return newList.findIndex(({ id }) => id === baseList);
    },
    changeVariableGroup(payload) {
      const destinyGroupIndex = this.indexFinder(this.groupList, payload.destinyGroupId);
      const originGroupIndex = this.indexFinder(this.groupList, payload.originGroupId);
      const variableIndex = this.indexFinder(this.variables, payload.variableId);
      this.groupList[destinyGroupIndex].variables
        .push(this.variables[variableIndex]);
      this.groupList[originGroupIndex].variables = this.groupList[originGroupIndex].variables
        .filter(({ id }) => id !== payload.variableId);
      this.$emit('change-variable-group-by-button', payload);
    },
    setVariableMovedPayload(payload) {
      this.variableMovedPayload = {
        ...this.variableMovedPayload,
        ...payload,
      };
    },
    handleSorted(payload) {
      this.setVariableMovedPayload(payload);
      this.$emit('moved-variable-by-drag', this.variableMovedPayload);
    },
    handleAdded(payload) {
      this.setVariableMovedPayload(payload);
    },
    handleRemoval(originGroupId) {
      this.setVariableMovedPayload({ originGroupId });
      this.$emit('moved-variable-by-drag', this.variableMovedPayload);
    },
    showAllHandler(value) {
      (this.$refs.accordion || []).forEach((component) => {
        component.collapseHandler(value);
      });
    },
    quitRenaming() {
      this.$emit('quit-renaming');
    },
    applyFilter(filter) {
      this.$emit('apply-filter', filter);
    },
    openSortGroupModal() {
      this.showSortGroupModal = true;
    },
    openDefaultGroupsModal() {
      this.showConfirmationRestoreDefaultGroupsModal = true;
    },
    closeModalRestoreGroups() {
      this.showConfirmationRestoreDefaultGroupsModal = false;
    },
    restoreDefaultGroupsModal() {
      this.$emit('restoreGroups', [this.assetModuleId]);
      this.closeModalRestoreGroups();
    },
    searchChange(search) {
      this.$emit('search-change', search);
    },
  },
};
</script>
